/* Serial Seal — tier controls (shared).
 *
 * WHY THIS EXISTS
 * Clarity replays showed real customers TAPPING the bulk-discount chips and
 * the volume table and getting nothing. A dead tap on a price is not a neutral
 * event: the customer has just told us which tier they want, and the site
 * answered by looking broken. This file turns every tier surface OUTSIDE the
 * builder into a real control that sets the quantity.
 *
 * IT INVENTS NO NUMBERS. Each control carries the pack count its own surface
 * already prints (`data-ss-packs`), rendered from that surface's existing
 * ladder data. Nothing here derives a price, a percentage or a tier — it only
 * asks the builder for a quantity in PACKS.
 *
 * ── THE CONTRACT WITH THE BUILDER (sections/sv-builder.liquid) ────────────
 * Two channels, in this order, so the feature works whether or not the
 * builder has shipped its half:
 *
 *   1. SAME PAGE — DOM event on `document`:
 *        document.dispatchEvent(new CustomEvent('ss:setpacks', {
 *          detail: { packs: N, source: 'volume' } }))
 *      The builder applies N packs (setPaid(N * PACK_PAID)) and acknowledges:
 *        document.dispatchEvent(new CustomEvent('ss:packs-applied', {
 *          detail: { packs: appliedPacks, scrolled: true|false } }))
 *      `scrolled:true` means the builder already brought the stepper into
 *      view, so we don't scroll a second time.
 *
 *   2. CROSS PAGE — URL: `/?packs=N#step2`
 *      The builder reads `packs` on boot. If it doesn't, the rescue at the
 *      bottom of this file applies it — but ONLY while the stepper is still
 *      untouched, so it can never overwrite a customer's own number.
 *
 * FALLBACK CHAIN when no acknowledgement arrives inside ACK_MS:
 *   window.setPaid(N * PACK_PAID)  →  window.setSeals(N * PACK_UNIT)  →  write
 *   #ss-qin in the unit its own step/min declare + fire `input`  →  reveal the
 *   stepper and say the number out loud.
 * Every rung is independent of the one above it, so this ships useful on its
 * own and gets better, not different, when the builder's half lands. The named
 * setters come first because their unit is in their signature; the raw input
 * is last because its unit is markup we do not own — see the PAID vs TOTAL
 * note on the constants below before changing this order.
 *
 * ── EVERY PRESS ACTS. IN BOTH DIRECTIONS. ────────────────────────────────
 * OWNER RULING, 2026-08-06 — do not undo this without asking him.
 *
 * This file used to gate REDUCTIONS behind a second press ("Lower to N? Tap
 * again"), on the theory that a stray tap on a low rung is a downsell. The
 * builder shipped the same idea in a harsher form — its chips moved the
 * quantity only UPWARD — and the owner hit the wall within minutes: once he
 * had climbed a few rungs, every chip below him was inert. "After the 3rd chip
 * click it stops letting me press."
 *
 * That is the exact bug this whole feature exists to fix. A customer who
 * presses a tier control has just TOLD US which quantity they want; answering
 * with nothing — or with a challenge — is the site looking broken at the one
 * moment the customer was being explicit. A quantity is also the cheapest
 * thing on the page to change back: nothing is ordered until they review it.
 *
 * So: `request()` applies on any press where the target differs from the
 * current quantity, up or down, first press, no confirmation. What protects
 * against the fat finger is VISIBILITY, not friction — a reduction scrolls the
 * stepper into view, flashes it, and is announced with BOTH numbers ("Lowered
 * from 300 seals to 12 seals"), so a mis-tap is obvious and one press from
 * being undone.
 *
 * DO NOT reintroduce any rule that makes a press do nothing.
 */
(function () {
  'use strict';
  if (window.SSTier) return;          /* two sections load this; one instance */

  /* Mirrors sv-builder.js. These are UNIT DEFINITIONS, not the discount
     ladder — a pack is 12 seals. The ladder itself lives in exactly one place
     per surface and is never recomputed here.

     ── PAID === TOTAL SINCE 2026-08-12 ─────────────────────────────────────
     The bonus pack ("10 paid + 2 free") is retired: the ladder now prices per
     SEAL, $0.60 down to $0.30, so a pack is simply 12 seals and PACK_PAID
     equals PACK_UNIT. This file kept PACK_PAID = 10 for a few hours after
     sv-builder.js moved to 12, and the mismatch was live: forceLocal() calls
     window.setPaid(packs * PACK_PAID), setPaid() divides by ITS OWN
     PACK_PAID, so every fallback tap sold 0.83x what was asked — a 600-seal
     chip landed on 504. THESE TWO CONSTANTS MUST MOVE TOGETHER, always.

     Everything below is still carried in PACKS — the one unit that is the
     same on every surface, is what the Shopify discounts count, and is what
     the cart line quantity is. Where a seal count has to be read or written,
     the unit is established first (see qinUnit / deqTotal) rather than
     guessed. */
  var PACK_PAID = 12;                        /* === PACK_UNIT; see above */
  var PACK_UNIT = 12;                        /* TOTAL seals per pack */
  /* Mirrors PAID_MAX in assets/sv-builder.js and must move with it: this file
     CLAMPS to MAX_PACKS before asking the builder, so a stale copy here would
     silently truncate a legitimate quantity (a 9,996-seal tap landing on 6,000)
     with no error anywhere. 8340 paid = 834 packs = 10,008 seals, the largest
     run production has been proven at. */
  var PAID_MAX  = 10008;   /* moved with sv-builder.js 2026-08-12: this is
                              in PACK_PAID units, and PACK_PAID is now 12 */
  var MAX_PACKS = PAID_MAX / PACK_PAID;      /* 834 — the self-serve ceiling */

  var ACK_MS = 420;        /* how long we wait for the builder to answer */
  var CONTACT = '/pages/contact';

  function reduced() {
    return window.matchMedia &&
           matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function qin()  { return document.getElementById('ss-qin'); }
  function qbox() { return document.getElementById('packs'); }
  function hasBuilder() { return !!(qin() || qbox()); }

  /* What unit does #ss-qin hold — PAID seals or TOTAL seals? The input DECLARES
     it in its own markup: a stepper counting paid seals moves in 10s from 10, a
     stepper counting totals moves in 12s from 12. We read that rather than
     assume, and return 0 for "it says neither" so callers can decline to write
     a number whose meaning they cannot establish. */
  function qinUnit() {
    var i = qin();
    if (!i) return 0;
    var step = parseInt(i.getAttribute('step'), 10);
    var min  = parseInt(i.getAttribute('min'), 10);
    if (step === PACK_UNIT || min === PACK_UNIT) return PACK_UNIT;
    if (step === PACK_PAID || min === PACK_PAID) return PACK_PAID;
    return 0;
  }

  /* The TOTAL seal count off the builder's own equation ("… = 120 total").
     renderPacks() rewrites this on every change, so it is the one number on the
     page that is unambiguously a total and unambiguously current. 0 = absent. */
  function deqTotal() {
    var t = document.querySelector('#ss-qdeq .qd-tot b');
    if (!t) return 0;
    var n = parseInt(String(t.textContent).replace(/[^0-9]/g, ''), 10);
    return n > 0 ? n : 0;
  }

  /* Current quantity in PACKS. Three sources, best first — each one is either
     already in packs or is a count whose unit is established, never a bare
     number divided by a hopeful constant. null = no builder / can't tell. */
  function currentPacks() {
    /* 1. the builder's own quote object: packs, stated as packs */
    if (typeof window.pack === 'function') {
      try {
        var q = window.pack();
        if (q && q.packs > 0) return q.packs;
      } catch (e) { /* fall through */ }
    }
    /* 2. the equation: a declared TOTAL */
    var tot = deqTotal();
    if (tot > 0) return Math.max(1, Math.round(tot / PACK_UNIT));
    /* 3. the raw input, in whichever unit it declares */
    var i = qin();
    if (!i) return null;
    var v = parseInt(i.value, 10);
    var u = qinUnit();
    if (isNaN(v) || v <= 0 || !u) return null;
    return Math.max(1, Math.round(v / u));
  }

  /* ——————————————————— chrome we own (injected, not in ss.css) ———————————
     Kept here rather than in a shared stylesheet so adding a tier surface is
     one data attribute and nothing else, and so this can't collide with
     another edit to ss.css. */
  var CSS =
    '[data-ss-packs]{position:relative;cursor:pointer;' +
      '-webkit-tap-highlight-color:transparent;' +
      'transition:background-color .16s ease,box-shadow .16s ease}' +
    '[data-ss-packs]:focus-visible{outline:2px solid #9ad7ff;outline-offset:2px;' +
      'border-radius:8px}' +
    /* the stepper, when a tier control has just driven it */
    '.ss-tier-flash{box-shadow:0 0 0 2px rgba(154,215,255,.9),' +
      '0 0 26px -4px rgba(154,140,255,.75)!important;border-radius:12px}' +
    '@media(prefers-reduced-motion:no-preference){' +
      '.ss-tier-flash{animation:ssTierFlash 1.1s ease-out 1}' +
      '@keyframes ssTierFlash{0%{box-shadow:0 0 0 5px rgba(154,215,255,.55),' +
        '0 0 30px -2px rgba(154,140,255,.9)}' +
        '100%{box-shadow:0 0 0 2px rgba(154,215,255,.9),' +
        '0 0 26px -4px rgba(154,140,255,.75)}}}' +
    '.ss-tier-live{position:absolute;width:1px;height:1px;overflow:hidden;' +
      'clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}';

  function injectCSS() {
    if (document.getElementById('ss-tier-css')) return;
    var s = document.createElement('style');
    s.id = 'ss-tier-css';
    s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  var liveEl = null;
  function announce(msg) {
    if (!liveEl) {
      liveEl = document.createElement('div');
      liveEl.className = 'ss-tier-live';
      liveEl.id = 'ss-tier-live';
      liveEl.setAttribute('aria-live', 'polite');
      liveEl.setAttribute('role', 'status');
      document.body.appendChild(liveEl);
    }
    /* re-announce an identical string: SRs skip unchanged text */
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = msg; }, 30);
  }

  /* ——————————————————— show the customer what changed ——————————————————— */
  function revealStepper() {
    var box = qbox() || document.getElementById('step2');
    if (!box) return;
    box.scrollIntoView({
      behavior: reduced() ? 'auto' : 'smooth',
      block: 'center'
    });
    var flash = qbox();
    if (!flash) return;
    flash.classList.remove('ss-tier-flash');
    void flash.offsetWidth;                       /* restart the animation */
    flash.classList.add('ss-tier-flash');
    clearTimeout(revealStepper._t);
    revealStepper._t = setTimeout(function () {
      flash.classList.remove('ss-tier-flash');
    }, 1800);
  }

  /* ——————————————————— applying a quantity ——————————————————— */

  /* Did the builder's own render actually take the quantity? Asked and answered
     in PACKS so the check cannot itself fall into the paid/total trap: reading
     back "120" proves nothing until you know whether 120 was meant to be the
     paid count or the total. null = can't tell. */
  function builderShows(packs) {
    var tot = deqTotal();
    if (tot > 0) return Math.round(tot / PACK_UNIT) === packs;
    if (typeof window.pack === 'function') {
      try {
        var q = window.pack();
        if (q && q.packs > 0) return q.packs === packs;
      } catch (e) { /* fall through */ }
    }
    return null;
  }

  /* Rungs 2 and 3 of the fallback chain — used only when the builder never
     answers `ss:setpacks`. Returns true if the quantity really moved.

     Order matters: the two named setters state their own unit in their
     signature, so they cannot be misread. Writing into #ss-qin is LAST because
     it is the only route whose meaning depends on markup we do not own. */
  function forceLocal(packs) {
    /* setPaid() takes PAID seals — that is its contract and it is staying that
       way even as the visible input switches to totals. */
    if (typeof window.setPaid === 'function') {
      try {
        window.setPaid(packs * PACK_PAID);
        if (builderShows(packs) !== false) return true;
      } catch (e) { /* fall through */ }
    }
    /* setSeals() takes TOTAL seals, if the builder grows one. */
    if (typeof window.setSeals === 'function') {
      try {
        window.setSeals(packs * PACK_UNIT);
        if (builderShows(packs) !== false) return true;
      } catch (e) { /* fall through */ }
    }

    var i = qin();
    if (!i) return false;
    var u = qinUnit();
    /* The input exists but will not say what it counts. Writing here would be
       a coin flip between the right quantity and 0.83x of it, so we decline and
       let the caller put the stepper in front of the customer instead. */
    if (!u) return builderShows(packs) === true;
    i.value = packs * u;
    i.dispatchEvent(new Event('input', { bubbles: true }));
    i.dispatchEvent(new Event('change', { bubbles: true }));
    var shown = builderShows(packs);
    if (shown !== null) return shown;
    return parseInt(i.value, 10) === packs * u;
  }

  function goToBuilder(packs) {
    location.href = '/?packs=' + packs + '#step2';
  }

  /* The one path every surface uses. `packs` is a whole pack count. */
  function apply(packs, source, label) {
    packs = Math.max(1, Math.min(MAX_PACKS, Math.round(packs)));

    if (!hasBuilder()) { goToBuilder(packs); return; }

    var settled = false;
    function onAck(e) {
      settled = true;
      document.removeEventListener('ss:packs-applied', onAck);
      var d = (e && e.detail) || {};
      if (!d.scrolled) revealStepper();
      syncAll();
      announce(label || ('Quantity set to ' + fmt(seals(packs)) + ' seals.'));
    }
    document.addEventListener('ss:packs-applied', onAck);

    document.dispatchEvent(new CustomEvent('ss:setpacks', {
      detail: { packs: packs, source: source || 'tier' }
    }));

    setTimeout(function () {
      if (settled) return;
      document.removeEventListener('ss:packs-applied', onAck);
      if (forceLocal(packs)) {
        revealStepper();
        syncAll();
        announce(label || ('Quantity set to ' + fmt(seals(packs)) + ' seals.'));
      } else {
        /* Deliberately NOT a page load. We are already on the page holding
           the customer's design; reloading to /?packs=N would throw that
           away to win a quantity. Put the stepper in front of them instead —
           worst case they type the number, best case nothing was lost. */
        revealStepper();
        announce('Set the quantity to ' + fmt(seals(packs)) +
          ' seals using the stepper.');
      }
    }, ACK_MS);
  }

  /* ——————————————————— asking for a quantity ———————————————————
     Everything the customer reads is TOTAL seals — the number they receive —
     never the paid count and never the pack count. `packs` stays the argument
     because packs is what we act on; seals is only ever what we SAY. */
  function seals(packs) { return packs * PACK_UNIT; }
  function fmt(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function request(packs, opts) {
    opts = opts || {};
    packs = Math.round(packs);
    if (!(packs > 0)) return;
    if (packs > MAX_PACKS) { location.href = CONTACT; return; }

    /* Every press acts, up or down — see the header. The only thing the old
       quantity is used for now is SAYING what happened: a reduction is
       announced with both numbers so a mis-tap is unmistakable and one press
       from being undone. It never decides whether to act. */
    var cur = currentPacks();
    var label = opts.label;
    if (!label && cur !== null && packs < cur) {
      label = 'Quantity lowered from ' + fmt(seals(cur)) + ' seals to ' +
              fmt(seals(packs)) + ' seals.';
    }
    apply(packs, opts.source, label);
  }

  /* ——————————————————— wiring a surface ——————————————————— */
  var groups = [];

  /* Light exactly ONE control per group: the highest rung the current
     quantity has reached. Same semantics as the builder's own chip strip, so
     the two never disagree about which tier the customer is on. */
  function syncGroup(g) {
    var cur = currentPacks();
    var win = -1;
    if (cur !== null) {
      g.items.forEach(function (it) {
        if (it.packs <= cur && it.packs > win) win = it.packs;
      });
    }
    g.items.forEach(function (it) {
      var on = it.packs === win;
      it.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      it.el.classList.toggle('is-on', on);
    });
  }
  function syncAll() { groups.forEach(syncGroup); }

  function wire(root, source) {
    if (!root) return;
    var nodes = root.querySelectorAll('[data-ss-packs]');
    if (!nodes.length) return;
    var g = { root: root, items: [] };

    Array.prototype.forEach.call(nodes, function (el) {
      var packs = parseInt(el.getAttribute('data-ss-packs'), 10);
      if (!(packs > 0)) return;

      var isLink = el.tagName === 'A' && el.hasAttribute('href');

      /* A rung past the self-serve ceiling must never offer an impossible
         quantity. Surfaces render those as a plain bulk-enquiry link; if one
         ever arrives without an href, send it there anyway. */
      if (packs > MAX_PACKS) {
        if (!isLink) {
          el.setAttribute('role', 'link');
          if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
          el.addEventListener('click', function () { location.href = CONTACT; });
        }
        return;
      }

      /* Keyboard: <button> and <a> already activate on Enter, and adding our
         own Enter handler on top of that fires the request twice. Links alone
         need Space taught to them, because role="button" promises it. */
      if (el.tagName === 'BUTTON') {
        /* nothing to add */
      } else if (isLink) {
        el.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); el.click(); }
        });
      } else {
        el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            el.click();
          }
        });
      }

      el.addEventListener('click', function (e) {
        /* open-in-new-tab and friends stay the browser's business */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
            (e.button != null && e.button > 0)) return;
        /* no builder on this page: the href IS the right behaviour */
        if (isLink && !hasBuilder()) return;
        e.preventDefault();
        request(packs, {
          el: el,
          source: source || 'tier',
          label: el.getAttribute('data-ss-say') || null
        });
      });

      g.items.push({ el: el, packs: packs });
    });

    if (!g.items.length) return;
    groups.push(g);
    syncGroup(g);
  }

  /* ——————————————————— staying in step with the builder ———————————————————
     renderPacks() writes #ss-qin.value directly, which fires no event and is
     invisible to a MutationObserver (it is a property, not an attribute). The
     price total beside it IS rewritten as text on every render, so watching
     that is the one reliable signal that the quantity moved. */
  function watchBuilder() {
    document.addEventListener('ss:packs-applied', syncAll);
    var i = qin();
    if (i) i.addEventListener('input', syncAll);
    var tot = document.getElementById('ss-qtot');
    if (tot && window.MutationObserver) {
      var t = null;
      new MutationObserver(function () {
        clearTimeout(t); t = setTimeout(syncAll, 60);
      }).observe(tot, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ——————————————————— ?packs=N rescue ———————————————————
     The builder owns this param. This is a safety net for the window before
     its half ships (and for any future regression): it only ever fires while
     the stepper is still at its untouched default, so it cannot overwrite a
     number the customer chose. */
  function rescueParam() {
    var m = /[?&]packs=(\d{1,4})(?!\d)/.exec(location.search);
    if (!m) return;
    var want = Math.max(1, Math.min(MAX_PACKS, parseInt(m[1], 10)));
    if (!want) return;
    setTimeout(function () {
      if (!hasBuilder()) return;
      /* compared in PACKS, so the test survives the input's unit changing */
      var cur = currentPacks();
      if (cur === want) { syncAll(); return; }            /* builder got it */
      if (cur !== null && cur !== 1) { syncAll(); return; } /* customer moved it */
      if (forceLocal(want)) { syncAll(); revealStepper(); }
    }, 700);
  }

  window.SSTier = {
    PACK_PAID: PACK_PAID,
    PACK_UNIT: PACK_UNIT,
    PAID_MAX: PAID_MAX,
    MAX_PACKS: MAX_PACKS,
    request: request,
    wire: wire,
    sync: syncAll,
    hasBuilder: hasBuilder,
    currentPacks: currentPacks,
    /* exposed for the live smoke test: which unit #ss-qin is speaking today */
    qinUnit: qinUnit
  };

  function boot() {
    injectCSS();
    watchBuilder();
    /* sections announce themselves; also pick up anything already parsed */
    document.querySelectorAll('[data-ss-tiers]').forEach(function (r) {
      if (r.dataset.ssWired) return;
      r.dataset.ssWired = '1';
      wire(r, r.getAttribute('data-ss-tiers') || 'tier');
    });
    rescueParam();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else boot();
})();
